import React, { useEffect, useRef, useState } from "react";
import { Crop, Mic, MonitorUp, Pause, Play, Square, UploadCloud, Video, Volume2, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { procedureRecordingsAPI } from "../services/api";
import ProcedureBotAvailabilityField from "./ProcedureBotAvailabilityField";

const RECORDING_SLICE_MS = 5000;
const OUTPUT_FPS = 30;
const MIN_CROP_SIZE = 0.05;
const VIDEO_BITRATE = 5000000;
const AUDIO_BITRATE = 128000;

const preferredMimeType = () => {
  const formats = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];

  return formats.find((format) => MediaRecorder.isTypeSupported(format)) || "";
};

const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hours, minutes, secs]
    .filter((value, index) => index > 0 || value > 0)
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

const blobFromCanvas = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.82));

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const isFullCaptureArea = (selectedCrop) =>
  selectedCrop.x <= 0.001 &&
  selectedCrop.y <= 0.001 &&
  selectedCrop.width >= 0.999 &&
  selectedCrop.height >= 0.999;

export default function ProcedureRecorderDialog({ ticket, onSaved, disabled, triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("setup");
  const [authorized, setAuthorized] = useState(true);
  const [includeSourceAudio, setIncludeSourceAudio] = useState(true);
  const [includeMicrophone, setIncludeMicrophone] = useState(true);
  const [sourceAudioAvailable, setSourceAudioAvailable] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [captureError, setCaptureError] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sharedWithTeam, setSharedWithTeam] = useState(true);
  const [availableInBot, setAvailableInBot] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [sourceSize, setSourceSize] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 1, height: 1 });

  const recorderRef = useRef(null);
  const displayStreamRef = useRef(null);
  const microphoneStreamRef = useRef(null);
  const outputStreamRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const previewRef = useRef(null);
  const cropPreviewRef = useRef(null);
  const previewBoxRef = useRef(null);
  const recordingIdRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const uploadQueueRef = useRef(Promise.resolve());
  const uploadFailureRef = useRef(null);
  const timerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const cropDragRef = useRef(null);
  const isStoppingMediaRef = useRef(false);

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = window.setInterval(() => {
      setRecordingTime((current) => current + 1);
    }, 1000);
  };

  const stopMedia = () => {
    if (isStoppingMediaRef.current) return;
    isStoppingMediaRef.current = true;
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    outputStreamRef.current?.getTracks().forEach((track) => track.stop());
    canvasStreamRef.current?.getTracks().forEach((track) => track.stop());
    displayStreamRef.current?.getTracks().forEach((track) => track.stop());
    microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioContextRef.current?.close().catch(() => {});

    outputStreamRef.current = null;
    canvasStreamRef.current = null;
    displayStreamRef.current = null;
    microphoneStreamRef.current = null;
    audioContextRef.current = null;

    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current.srcObject = null;
    }
    if (cropPreviewRef.current) {
      cropPreviewRef.current.pause();
      cropPreviewRef.current.srcObject = null;
    }
    isStoppingMediaRef.current = false;
  };

  const resetDialog = () => {
    stopTimer();
    recorderRef.current = null;
    recordingIdRef.current = null;
    chunkIndexRef.current = 0;
    uploadQueueRef.current = Promise.resolve();
    uploadFailureRef.current = null;
    cropDragRef.current = null;
    setStage("setup");
    setAuthorized(true);
    setIncludeSourceAudio(true);
    setIncludeMicrophone(true);
    setSourceAudioAvailable(false);
    setIsStarting(false);
    setIsSaving(false);
    setIsPaused(false);
    setRecordingTime(0);
    setUploadedBytes(0);
    setCaptureError("");
    setTitle("");
    setDescription("");
    setSharedWithTeam(true);
    setAvailableInBot(false);
    setThumbnail(null);
    setSourceSize({ width: 0, height: 0 });
    setCrop({ x: 0, y: 0, width: 1, height: 1 });
  };

  useEffect(() => {
    return () => {
      stopTimer();
      stopMedia();
    };
  }, []);

  useEffect(() => {
    if (stage !== "crop" || !previewRef.current || !cropPreviewRef.current || !displayStreamRef.current) return undefined;

    const preview = previewRef.current;
    const cropPreview = cropPreviewRef.current;
    preview.srcObject = displayStreamRef.current;
    cropPreview.srcObject = displayStreamRef.current;
    const updateDimensions = () => {
      if (preview.videoWidth && preview.videoHeight) {
        setSourceSize({ width: preview.videoWidth, height: preview.videoHeight });
      }
    };
    preview.addEventListener("loadedmetadata", updateDimensions);
    preview.addEventListener("resize", updateDimensions);
    cropPreview.addEventListener("loadedmetadata", updateDimensions);
    preview.play().catch(() => {});
    cropPreview.play().catch(() => {});
    updateDimensions();

    return () => {
      preview.removeEventListener("loadedmetadata", updateDimensions);
      preview.removeEventListener("resize", updateDimensions);
      cropPreview.removeEventListener("loadedmetadata", updateDimensions);
    };
  }, [stage]);

  const uploadChunkWithRetry = async (recordingId, chunkIndex, chunk) => {
    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        return await procedureRecordingsAPI.uploadChunk(recordingId, chunkIndex, chunk);
      } catch (error) {
        lastError = error;
        if (attempt < 2) await wait((attempt + 1) * 1000);
      }
    }
    throw lastError;
  };

  const captureThumbnail = async () => {
    const preview = previewRef.current;
    if (!preview || !preview.videoWidth || !preview.videoHeight) return null;

    const maxWidth = 960;
    const cropWidth = Math.max(1, Math.round(preview.videoWidth * crop.width));
    const cropHeight = Math.max(1, Math.round(preview.videoHeight * crop.height));
    const scale = Math.min(1, maxWidth / cropWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(cropWidth * scale));
    canvas.height = Math.max(1, Math.round(cropHeight * scale));
    canvas.getContext("2d").drawImage(
      preview,
      Math.round(preview.videoWidth * crop.x),
      Math.round(preview.videoHeight * crop.y),
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    return blobFromCanvas(canvas);
  };

  const queueChunk = (chunk) => {
    if (!chunk?.size || !recordingIdRef.current) return;

    const recordingId = recordingIdRef.current;
    const chunkIndex = chunkIndexRef.current;
    chunkIndexRef.current += 1;

    uploadQueueRef.current = uploadQueueRef.current.then(async () => {
      if (uploadFailureRef.current) return;
      try {
        const response = await uploadChunkWithRetry(recordingId, chunkIndex, chunk);
        setUploadedBytes(response.data.bytes_received || 0);
      } catch (error) {
        uploadFailureRef.current = error;
        setCaptureError("O envio da gravação falhou. A captura será interrompida para não perder mais conteúdo.");
        if (recorderRef.current?.state !== "inactive") {
          setStage("stopping");
          recorderRef.current.stop();
        }
      }
    });
  };

  const onRecorderStopped = async () => {
    stopTimer();
    setIsPaused(false);
    setStage("preparing");
    let previewThumbnail = null;
    try {
      previewThumbnail = await captureThumbnail();
    } catch (error) {
      console.warn("Não foi possível gerar a miniatura da gravação", error);
    }
    setThumbnail(previewThumbnail);
    stopMedia();

    await uploadQueueRef.current;
    if (uploadFailureRef.current) {
      const recordingId = recordingIdRef.current;
      if (recordingId) {
        try {
          await procedureRecordingsAPI.discardDraft(recordingId);
        } catch (_) {
          // The scheduled backend cleanup removes an abandoned draft if the connection is down.
        }
      }
      recordingIdRef.current = null;
      setCaptureError("Não foi possível enviar toda a gravação. Verifique a conexão e grave novamente.");
      setStage("setup");
      return;
    }

    setTitle(`Procedimento — ${ticket.protocol || "Atendimento"}`);
    setStage("details");
  };

  const cancelSelectedSource = () => {
    stopMedia();
    setCaptureError("");
    setStage("setup");
    setSourceSize({ width: 0, height: 0 });
  };

  const selectSource = async () => {
    if (!authorized) {
      setCaptureError("Confirme que o cliente autorizou e que a janela correta será gravada.");
      return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia || !window.MediaRecorder) {
      setCaptureError("Este navegador não suporta captura de janela. Use Chrome ou Edge atualizado.");
      return;
    }

    setCaptureError("");
    setIsStarting(true);
    let displayStream;
    let microphoneStream;

    try {
      // This must happen directly from the click: browsers deliberately show their source picker.
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: OUTPUT_FPS, max: OUTPUT_FPS },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: includeSourceAudio,
        preferCurrentTab: false,
        selfBrowserSurface: "exclude",
        surfaceSwitching: "include",
        systemAudio: includeSourceAudio ? "include" : "exclude",
        windowAudio: includeSourceAudio ? "window" : "exclude",
        audioSelection: includeSourceAudio ? "preferred" : undefined,
      });

      if (includeMicrophone) {
        microphoneStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
      }

      displayStreamRef.current = displayStream;
      microphoneStreamRef.current = microphoneStream || null;
      setSourceAudioAvailable(displayStream.getAudioTracks().length > 0);
      setCrop({ x: 0, y: 0, width: 1, height: 1 });
      setStage("crop");

      displayStream.getVideoTracks()[0]?.addEventListener("ended", () => {
        if (isStoppingMediaRef.current) return;
        if (recorderRef.current?.state && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        } else {
          cancelSelectedSource();
        }
      });
    } catch (error) {
      displayStream?.getTracks().forEach((track) => track.stop());
      microphoneStream?.getTracks().forEach((track) => track.stop());
      const denied = error?.name === "NotAllowedError";
      setCaptureError(
        denied
          ? includeMicrophone
            ? "A janela ou o microfone não foram autorizados. Ajuste as opções e tente novamente."
            : "A gravação não começou porque nenhuma janela foi autorizada."
          : error?.response?.data?.detail || "Não foi possível selecionar a fonte da gravação.",
      );
      stopMedia();
    } finally {
      setIsStarting(false);
    }
  };

  const startCanvasLoop = (canvas, preview, selectedCrop) => {
    const context = canvas.getContext("2d", { alpha: false });
    const paint = () => {
      if (!preview.videoWidth || !preview.videoHeight) return;
      context.drawImage(
        preview,
        Math.round(preview.videoWidth * selectedCrop.x),
        Math.round(preview.videoHeight * selectedCrop.y),
        Math.max(1, Math.round(preview.videoWidth * selectedCrop.width)),
        Math.max(1, Math.round(preview.videoHeight * selectedCrop.height)),
        0,
        0,
        canvas.width,
        canvas.height,
      );
      animationFrameRef.current = window.requestAnimationFrame(paint);
    };
    paint();
  };

  const createRecordingStream = (preview, selectedCrop) => {
    const sourceVideoTrack = displayStreamRef.current?.getVideoTracks()[0];
    const useDirectSource = Boolean(sourceVideoTrack && isFullCaptureArea(selectedCrop));
    let outputTracks;

    if (useDirectSource) {
      // Avoid a canvas/requestAnimationFrame hop when there is no crop. Windows can
      // throttle that hop while a desktop application is in the foreground, which
      // can leave legacy applications looking frozen in an otherwise valid capture.
      canvasStreamRef.current = null;
      outputTracks = [sourceVideoTrack];
    } else {
      const cropWidth = Math.max(2, Math.round(preview.videoWidth * selectedCrop.width));
      const cropHeight = Math.max(2, Math.round(preview.videoHeight * selectedCrop.height));
      const canvas = document.createElement("canvas");
      canvas.width = cropWidth - (cropWidth % 2);
      canvas.height = cropHeight - (cropHeight % 2);
      startCanvasLoop(canvas, preview, selectedCrop);

      const canvasStream = canvas.captureStream(OUTPUT_FPS);
      canvasStreamRef.current = canvasStream;
      outputTracks = [...canvasStream.getVideoTracks()];
    }
    const audioTracks = [
      ...(includeSourceAudio ? displayStreamRef.current?.getAudioTracks() || [] : []),
      ...(includeMicrophone ? microphoneStreamRef.current?.getAudioTracks() || [] : []),
    ];

    if (audioTracks.length) {
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      audioTracks.forEach((track) => {
        const source = audioContext.createMediaStreamSource(new MediaStream([track]));
        source.connect(destination);
      });
      audioContext.resume().catch(() => {});
      audioContextRef.current = audioContext;
      outputTracks.push(...destination.stream.getAudioTracks());
    }

    const outputStream = new MediaStream(outputTracks);
    outputStreamRef.current = outputStream;
    return outputStream;
  };

  const beginRecording = async () => {
    const preview = previewRef.current;
    if (!preview?.videoWidth || !preview?.videoHeight) {
      setCaptureError("A prévia ainda está carregando. Aguarde alguns segundos.");
      return;
    }

    setCaptureError("");
    setIsStarting(true);
    try {
      const outputStream = createRecordingStream(preview, crop);
      const mimeType = preferredMimeType();
      const recorder = new MediaRecorder(
        outputStream,
        mimeType
          ? { mimeType, videoBitsPerSecond: VIDEO_BITRATE, audioBitsPerSecond: AUDIO_BITRATE }
          : { videoBitsPerSecond: VIDEO_BITRATE, audioBitsPerSecond: AUDIO_BITRATE },
      );
      const startResponse = await procedureRecordingsAPI.start(
        ticket.id,
        recorder.mimeType || mimeType || "video/webm",
      );

      recordingIdRef.current = startResponse.data.id;
      chunkIndexRef.current = 0;
      uploadQueueRef.current = Promise.resolve();
      uploadFailureRef.current = null;
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => queueChunk(event.data);
      recorder.onstop = onRecorderStopped;
      recorder.start(RECORDING_SLICE_MS);
      setRecordingTime(0);
      setUploadedBytes(0);
      setStage("recording");
      startTimer();
    } catch (error) {
      stopMedia();
      setCaptureError(error?.response?.data?.detail || "Não foi possível iniciar a gravação.");
      setStage("setup");
    } finally {
      setIsStarting(false);
    }
  };

  const pauseCapture = () => {
    if (recorderRef.current?.state !== "recording") return;
    recorderRef.current.pause();
    setIsPaused(true);
    stopTimer();
  };

  const resumeCapture = () => {
    if (recorderRef.current?.state !== "paused") return;
    recorderRef.current.resume();
    setIsPaused(false);
    startTimer();
  };

  const stopCapture = () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return;
    setStage("stopping");
    recorderRef.current.stop();
  };

  const discardDraft = async () => {
    const recordingId = recordingIdRef.current;
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      if (recorder.state !== "inactive") recorder.stop();
    }
    stopMedia();
    stopTimer();
    if (recordingId) {
      try {
        await procedureRecordingsAPI.discardDraft(recordingId);
      } catch (_) {
        // The scheduled backend cleanup handles a draft that could not be deleted now.
      }
    }
    resetDialog();
    setOpen(false);
  };

  const saveProcedure = async () => {
    if (!title.trim()) {
      setCaptureError("Informe um título para localizar o procedimento depois.");
      return;
    }
    if (!recordingIdRef.current) return;

    setCaptureError("");
    setIsSaving(true);
    try {
      const response = await procedureRecordingsAPI.complete(recordingIdRef.current, {
        title: title.trim(),
        description: description.trim(),
        durationSeconds: recordingTime,
        sharedWithTeam,
        availableInBot,
        thumbnail,
      });
      onSaved?.(response.data);
      stopMedia();
      resetDialog();
      setOpen(false);
    } catch (error) {
      setCaptureError(error?.response?.data?.detail || "Não foi possível salvar o procedimento.");
    } finally {
      setIsSaving(false);
    }
  };

  const getPointerPosition = (event) => {
    const bounds = previewBoxRef.current.getBoundingClientRect();
    return {
      x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
      y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
    };
  };

  const handleCropPointerDown = (event) => {
    if (!previewBoxRef.current) return;
    const point = getPointerPosition(event);
    const handle = event.target.dataset.cropHandle;
    const isInsideCrop =
      point.x >= crop.x && point.x <= crop.x + crop.width && point.y >= crop.y && point.y <= crop.y + crop.height;
    const mode = handle || (isInsideCrop ? "move" : "create");
    cropDragRef.current = { mode, start: point, crop };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCropPointerMove = (event) => {
    const drag = cropDragRef.current;
    if (!drag) return;
    const point = getPointerPosition(event);
    const original = drag.crop;

    if (drag.mode === "create") {
      const left = clamp(Math.min(drag.start.x, point.x), 0, 1 - MIN_CROP_SIZE);
      const top = clamp(Math.min(drag.start.y, point.y), 0, 1 - MIN_CROP_SIZE);
      const right = clamp(Math.max(drag.start.x, point.x), left + MIN_CROP_SIZE, 1);
      const bottom = clamp(Math.max(drag.start.y, point.y), top + MIN_CROP_SIZE, 1);
      setCrop({ x: left, y: top, width: right - left, height: bottom - top });
      return;
    }

    if (drag.mode === "move") {
      const x = clamp(original.x + point.x - drag.start.x, 0, 1 - original.width);
      const y = clamp(original.y + point.y - drag.start.y, 0, 1 - original.height);
      setCrop({ ...original, x, y });
      return;
    }

    let left = original.x;
    let right = original.x + original.width;
    let top = original.y;
    let bottom = original.y + original.height;
    if (drag.mode.includes("w")) left = clamp(point.x, 0, right - MIN_CROP_SIZE);
    if (drag.mode.includes("e")) right = clamp(point.x, left + MIN_CROP_SIZE, 1);
    if (drag.mode.includes("n")) top = clamp(point.y, 0, bottom - MIN_CROP_SIZE);
    if (drag.mode.includes("s")) bottom = clamp(point.y, top + MIN_CROP_SIZE, 1);
    setCrop({ x: left, y: top, width: right - left, height: bottom - top });
  };

  const handleCropPointerUp = () => {
    cropDragRef.current = null;
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && stage === "crop") {
      cancelSelectedSource();
      setOpen(false);
      return;
    }
    if (!nextOpen && ["recording", "stopping", "preparing", "details"].includes(stage)) return;
    setOpen(nextOpen);
    if (!nextOpen) {
      stopMedia();
      resetDialog();
    }
  };

  const formattedBytes = uploadedBytes
    ? `${(uploadedBytes / (1024 * 1024)).toFixed(1)} MB enviados`
    : "Preparando envio direto";
  const cropPercent = `${Math.round(crop.width * 100)}% × ${Math.round(crop.height * 100)}%`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          disabled={disabled}
          aria-label="Gravar procedimento"
          title="Gravar procedimento"
          className={`bg-violet-600 hover:bg-violet-700 text-white shadow-sm ${triggerClassName}`}
        >
          <Video className="w-4 h-4" />
          <span className="hidden md:inline">Gravar</span>
        </Button>
      </DialogTrigger>
      <DialogContent
        className={`max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto rounded-xl ${stage === "crop" ? "sm:max-w-4xl" : "sm:max-w-lg"}`}
        onEscapeKeyDown={(event) => {
          if (stage !== "setup") event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (stage !== "setup") event.preventDefault();
        }}
      >
        <video ref={previewRef} muted playsInline aria-hidden className="fixed h-px w-px opacity-0 pointer-events-none -left-10 -top-10" />
        {stage === "setup" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><MonitorUp className="w-5 h-5 text-violet-600" /> Gravar procedimento</DialogTitle>
              <DialogDescription>O vídeo ficará vinculado ao protocolo {ticket.protocol || "deste atendimento"} e ao cliente {ticket.client_name || "selecionado"}.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="rounded-lg border border-violet-100 bg-violet-50 p-3 text-sm text-violet-950 space-y-1">
                <p className="font-semibold">Selecione a janela do AnyDesk e ajuste o recorte.</p>
                <p>Depois do seletor do navegador, você poderá marcar exatamente a área que será gravada.</p>
              </div>
              <label className="flex gap-3 rounded-lg border p-3 text-sm cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={authorized} onChange={(event) => setAuthorized(event.target.checked)} className="mt-0.5 h-4 w-4 accent-violet-600" />
                <span>Tenho autorização para gravar este procedimento e conferirei a janela e o conteúdo selecionados.</span>
              </label>
              <div className="rounded-lg border divide-y">
                <label className="flex gap-3 p-3 text-sm cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={includeSourceAudio} onChange={(event) => setIncludeSourceAudio(event.target.checked)} className="mt-0.5 h-4 w-4 accent-violet-600" />
                  <span><strong className="flex items-center gap-1"><Volume2 className="w-4 h-4" /> Áudio da sessão</strong><span className="text-gray-500">Solicita o áudio da janela/sistema quando o navegador disponibilizar essa opção.</span></span>
                </label>
                <label className="flex gap-3 p-3 text-sm cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={includeMicrophone} onChange={(event) => setIncludeMicrophone(event.target.checked)} className="mt-0.5 h-4 w-4 accent-violet-600" />
                  <span><strong className="flex items-center gap-1"><Mic className="w-4 h-4" /> Minha voz</strong><span className="text-gray-500">Pede permissão do microfone e mistura sua explicação ao vídeo.</span></span>
                </label>
              </div>
              {captureError && <p className="text-sm text-red-600">{captureError}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={isStarting}>Cancelar</Button>
                <Button onClick={selectSource} disabled={isStarting} className="bg-violet-600 hover:bg-violet-700">{isStarting ? "Abrindo seletor..." : "Escolher janela"}</Button>
              </div>
            </div>
          </>
        )}

        {stage === "crop" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Crop className="w-5 h-5 text-violet-600" /> Ajustar área gravada</DialogTitle>
              <DialogDescription>Arraste o quadro para mover a área ou use os cantos para redimensioná-la. Apenas essa parte será salva.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="rounded-xl bg-black p-2 sm:p-3">
                <div
                  ref={previewBoxRef}
                  className="relative mx-auto max-h-[52vh] max-w-full select-none touch-none overflow-hidden bg-black"
                  style={{ aspectRatio: sourceSize.width && sourceSize.height ? `${sourceSize.width}/${sourceSize.height}` : "16/9" }}
                  onPointerDown={handleCropPointerDown}
                  onPointerMove={handleCropPointerMove}
                  onPointerUp={handleCropPointerUp}
                  onPointerCancel={handleCropPointerUp}
                >
                  <video ref={cropPreviewRef} muted playsInline className="pointer-events-none h-full w-full object-contain" />
                  <div
                    className="absolute border-2 border-violet-300 bg-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.46)] cursor-move"
                    style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` }}
                  >
                    <span className="absolute left-2 top-2 rounded bg-violet-700/90 px-2 py-1 text-xs font-semibold text-white">Área gravada</span>
                    {["nw", "ne", "sw", "se"].map((handle) => (
                      <span
                        key={handle}
                        data-crop-handle={handle}
                        className={`absolute h-4 w-4 rounded-sm border-2 border-white bg-violet-600 shadow cursor-pointer ${handle.includes("n") ? "-top-2" : "-bottom-2"} ${handle.includes("w") ? "-left-2" : "-right-2"}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-800">Recorte: {cropPercent}</span>
                {includeSourceAudio && <span className={`rounded-full px-3 py-1 ${sourceAudioAvailable ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}>{sourceAudioAvailable ? "Áudio da sessão detectado" : "Navegador não forneceu áudio da sessão"}</span>}
                {includeMicrophone && <span className="rounded-full bg-green-50 px-3 py-1 text-green-800">Microfone incluído</span>}
              </div>
              {captureError && <p className="text-sm text-red-600">{captureError}</p>}
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={cancelSelectedSource} disabled={isStarting}>Escolher outra janela</Button>
                <Button onClick={beginRecording} disabled={isStarting || !sourceSize.width} className="bg-violet-600 hover:bg-violet-700">{isStarting ? "Preparando..." : "Iniciar gravação"}</Button>
              </div>
            </div>
          </>
        )}

        {["recording", "stopping", "preparing"].includes(stage) && (
          <>
            <DialogHeader>
              <DialogTitle>Gravação do procedimento</DialogTitle>
              <DialogDescription>O vídeo mostra somente o recorte definido e é enviado em partes enquanto acontece.</DialogDescription>
            </DialogHeader>
            <div className="rounded-xl border border-red-100 bg-red-50 p-5 text-center space-y-3">
              <div className="flex items-center justify-center gap-2 text-red-700 font-mono text-3xl font-bold">
                {stage === "recording" && !isPaused && <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />}
                {formatDuration(recordingTime)}
              </div>
              <p className="text-sm text-red-700">{stage === "recording" ? formattedBytes : "Finalizando envio e preparando miniatura..."}</p>
            </div>
            {stage === "recording" && (
              <div className="flex justify-between gap-2 pt-2">
                <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={discardDraft}><X className="w-4 h-4 mr-2" /> Descartar</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={isPaused ? resumeCapture : pauseCapture}>{isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}{isPaused ? "Retomar" : "Pausar"}</Button>
                  <Button className="bg-red-600 hover:bg-red-700" onClick={stopCapture}><Square className="w-4 h-4 mr-2" /> Parar</Button>
                </div>
              </div>
            )}
          </>
        )}

        {stage === "details" && (
          <>
            <DialogHeader>
              <DialogTitle>Salvar procedimento</DialogTitle>
              <DialogDescription>O vídeo comprimido já foi enviado. Complete os dados para disponibilizá-lo.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm">
                <span><strong>Cliente:</strong> {ticket.client_name}</span>
                <span><strong>Protocolo:</strong> {ticket.protocol}</span>
                <span><strong>Duração:</strong> {formatDuration(recordingTime)}</span>
                <span><strong>Formato:</strong> WebM comprimido</span>
              </div>
              <div className="space-y-2"><Label htmlFor="procedure-title">Título</Label><Input id="procedure-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} autoFocus /></div>
              <div className="space-y-2"><Label htmlFor="procedure-description">Descrição</Label><Textarea id="procedure-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={4000} rows={4} placeholder="Explique o problema resolvido e os passos importantes..." /></div>
              <label className="flex items-center gap-3 rounded-lg border p-3 text-sm cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={sharedWithTeam} onChange={(event) => setSharedWithTeam(event.target.checked)} className="h-4 w-4 accent-violet-600" />
                <span><strong>Disponibilizar para a equipe.</strong> Outros atendentes autenticados poderão reutilizar o vídeo.</span>
              </label>
              <ProcedureBotAvailabilityField checked={availableInBot} onChange={setAvailableInBot} disabled={isSaving} />
              {captureError && <p className="text-sm text-red-600">{captureError}</p>}
              <div className="flex justify-between gap-2">
                <Button variant="ghost" className="text-red-600" onClick={discardDraft} disabled={isSaving}>Descartar</Button>
                <Button onClick={saveProcedure} disabled={isSaving} className="bg-violet-600 hover:bg-violet-700">{isSaving ? <><UploadCloud className="w-4 h-4 mr-2 animate-pulse" /> Salvando...</> : "Salvar procedimento"}</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
